import React, { useContext, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import MainLayouts from "../../layout/MainLayouts";
import CourseCard2 from "./CourseCard2";
import client from "../../api/client";
import { GlobalContext } from "../../context";

export default function CoursePage() {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const { userLocation } = useContext(GlobalContext);
  const isNG = userLocation?.data === "NG";
  const [category, setCategory] = useState(null);
  const [categoryCourses, setCategoryCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      client.get('/v2/categories'),
      client.get(`/v2/courses?category=${uuid}`)
    ]).then(([catRes, courseRes]) => {
      // Find category by id
      const catData = Array.isArray(catRes.data) ? catRes.data : (catRes.data.data || []);
      const cat = catData.find(c => c.slug === uuid || c.id.toString() === uuid);
      setCategory(cat ? {
        id: cat.id.toString(),
        slug: cat.slug,
        title: cat.name,
        desc: cat.description || "Explore this category to find the perfect course for your career goals.",
        image: cat.image_url || "https://images.unsplash.com/photo-1550439062-609e1531270e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
      } : { id: uuid, title: "Category", desc: "", image: "" });

      const courseList = [...(Array.isArray(courseRes.data) ? courseRes.data : (courseRes.data.data || []))].reverse();
      setCategoryCourses(courseList.map(course => ({
        id: course.id,
        title: course.title,
        desc: course.description || "",
        difficulty: course.course_level || "Beginner",
        cost: isNG ? `₦${Number(course.price_ngn || 0).toLocaleString()}` : `$${Number(course.price_usd || 0).toLocaleString()}`,
        duration: course.duration || "12 Weeks",
        image: course.image_url || "/default-course.jpg",
        format: "online",
        slug: course.slug
      })));
    }).catch(err => {
      console.error("Error fetching course category data", err);
      setCategory({ id: uuid, title: "Category", desc: "", image: "" });
    }).finally(() => {
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid]);

  if (loading) {
    return (
      <MainLayouts>
        <div className="min-h-[60vh] flex items-center justify-center bg-[#f4f6f9]">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </MainLayouts>
    );
  }

  return (
    <MainLayouts>
      <div className="bg-[#f4f6f9] min-h-screen">
        {/* Category Hero Section */}
        <div className="relative pt-32 pb-24 lg:pt-40 lg:pb-32 bg-[#0b0f19] overflow-hidden">
          {/* Background Image & Overlay */}
          <div className="absolute inset-0">
            <img src={category.image} alt={category.title} className="w-full h-full object-cover opacity-20" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0b0f19] to-transparent"></div>
          </div>

          <div className="relative z-10 max-w-[1560px] mx-auto px-6">
            <button
              onClick={() => navigate('/courses/categories')}
              className="mb-8 w-12 h-12 rounded-full bg-white/10 hover:bg-primary backdrop-blur-md border border-white/20 flex items-center justify-center text-white transition-colors duration-300"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
            </button>
            
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-8">
              <div className="max-w-3xl">
                <div className="text-[11px] font-bold tracking-[0.15em] uppercase text-primary mb-6 flex items-center gap-3">
                  <span className="block w-8 h-[2px] bg-primary"></span>
                  CATEGORY
                </div>
                <h1 className="text-white text-4xl md:text-6xl font-bold mb-6 leading-tight">
                  {category.title}<span className="text-primary">.</span>
                </h1>
                <p className="text-gray-400 text-lg md:text-xl leading-relaxed">
                  {category.desc}
                </p>
              </div>
              
              <div className="flex items-center gap-4 bg-white/5 backdrop-blur-md px-6 py-4 rounded-2xl border border-white/10 shrink-0">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center text-primary">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-6 h-6"><path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" /></svg>
                </div>
                <div>
                  <div className="text-white font-bold text-2xl">{categoryCourses.length}</div>
                  <div className="text-gray-400 text-xs font-bold uppercase tracking-wider">Total Courses</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Courses Listing */}
        <div className="py-24 max-w-[1560px] mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12 gap-6">
            <div>
              <h2 className="text-3xl font-bold text-[#0b0f19] mb-2">Available Programs</h2>
              <p className="text-gray-500 font-medium">Select a course to view details and enroll.</p>
            </div>
          </div>

          {categoryCourses.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {categoryCourses.map((course) => (
                <CourseCard2 key={course.id} course={course} />
              ))}
            </div>
          ) : (
            <div className="py-20 text-center bg-white rounded-3xl border border-gray-100 shadow-sm">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" className="w-16 h-16 text-gray-300 mx-auto mb-4"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
              <h3 className="text-xl font-bold text-[#0b0f19] mb-2">No Courses Found</h3>
              <p className="text-gray-500 max-w-md mx-auto">We are currently updating our curriculum for this category. Check back soon for new programs.</p>
            </div>
          )}
        </div>
      </div>
    </MainLayouts>
  );
}
